/** Sprint 5-d PR-5 Scope 9 · Chat 色系 toggle（暗/亮/跟隨系統）· MUI ThemeProvider dynamic swap。

僅套用於 AIChatLayout（chat 全螢幕·自成一 app）·不影響其餘走 MainLayout 的頁面。
偏好存 localStorage（per-viewer·三態 light/dark/system·system 讀 OS prefers-color-scheme）。
*/
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';

export type ThemeMode = 'light' | 'dark' | 'system';
const KEY = 'ai_chat_theme_mode';

interface Ctx { mode: ThemeMode; setMode: (m: ThemeMode) => void; }
const ChatThemeCtx = createContext<Ctx>({ mode: 'system', setMode: () => {} });
export const useChatTheme = () => useContext(ChatThemeCtx);

function readMode(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* silent ok: 私密視窗 localStorage 讀取失敗·fallback system */ }
  return 'system';
}

function prefersDark(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch { return false; }
}

export const ChatThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  // 「跟隨系統」時 reactive 追蹤 OS 切換
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSystemDark(mq.matches);
    try { mq.addEventListener('change', on); } catch { mq.addListener(on); }
    return () => { try { mq.removeEventListener('change', on); } catch { mq.removeListener(on); } };
  }, []);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try { localStorage.setItem(KEY, m); } catch { /* silent ok: 私密視窗無法寫入·僅本次 session 生效 */ }
  }, []);

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;
  const theme = useMemo(() => createTheme({ palette: { mode: resolved } }), [resolved]);

  return (
    <ChatThemeCtx.Provider value={{ mode, setMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ChatThemeCtx.Provider>
  );
};
